import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { AllMembersDirectory } from "../components/AllMembersDirectory";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, ExternalLink, Building2, Users, ArrowRight } from "lucide-react";

export default function PublicHome() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const totalMembers = clubs.reduce((s, c) => s + (c.member_count || 0), 0);

  useEffect(() => {
    api.get("/public/clubs").then((res) => setClubs(res.data)).finally(() => setLoading(false));
  }, []);

  const openSchool = (id) => navigate(`/skola/${id}`);

  const openSite = (url) => {
    if (!url) return;
    const full = url.startsWith("http") ? url : `https://${url}`;
    window.open(full, "_blank", "noopener");
  };

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 relative z-10">
        <div className="grid grid-cols-2 gap-px bg-border border-2 border-primary/60 max-w-2xl">
          <div className="bg-card p-4 sm:p-5 text-center" data-testid="stat-active-schools">
            <div className="font-display text-3xl sm:text-4xl font-black text-primary tracking-tighter">{clubs.length}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1.5">Aktivnih škola</div>
          </div>
          <div className="bg-card p-4 sm:p-5 text-center" data-testid="stat-total-members">
            <div className="font-display text-3xl sm:text-4xl font-black text-primary tracking-tighter">{totalMembers}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1.5">Ukupno izvođača</div>
          </div>
        </div>
      </section>

      {/* Directory */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex items-end justify-between mb-8 border-b-2 border-foreground pb-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Klubovi članice</h2>
          <span className="text-sm text-muted-foreground" data-testid="clubs-count">
            {clubs.length} {clubs.length === 1 ? "klub" : "klubova"}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Učitavanje...</div>
        ) : clubs.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="no-clubs">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-40" />
            Trenutno nema odobrenih klubova.
          </div>
        ) : (
          <>
            {/* Table header (desktop) */}
            <div className="hidden md:grid grid-cols-[60px_96px_1fr_1fr_1fr_140px] gap-4 px-4 py-3 text-xs uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
              <div>Rb.</div>
              <div>Logo</div>
              <div>Ime kluba</div>
              <div>Grad</div>
              <div>Email</div>
              <div>Telefon</div>
            </div>
            <div className="space-y-2.5 mt-3">
              {clubs.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className="dove-card grid grid-cols-2 md:grid-cols-[60px_96px_1fr_1fr_1fr_140px] gap-2 md:gap-4 px-4 py-4 items-center border border-white/10 transition-colors group"
                  data-testid={`club-row-${c.id}`}
                >
                  <div className="font-display font-bold text-lg text-primary md:col-auto">
                    {String(c.serial_no).padStart(2, "0")}
                  </div>
                  <button
                    onClick={() => openSchool(c.id)}
                    className="w-20 h-20 border border-border overflow-hidden bg-muted flex items-center justify-center hover:ring-2 ring-primary transition-all"
                    data-testid={`club-logo-${c.id}`}
                  >
                    {c.logo_path ? (
                      <img src={fileUrl(c.logo_path)} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className="col-span-2 md:col-auto">
                    <button
                      onClick={() => openSchool(c.id)}
                      className="font-display font-bold text-base sm:text-lg text-left hover:text-primary transition-colors flex items-center gap-1.5"
                      data-testid={`club-name-${c.id}`}
                    >
                      {c.name}
                      <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary" data-testid={`club-members-${c.id}`}>
                        <Users className="w-3.5 h-3.5" /> aktivno {c.member_count || 0} izvođača
                      </span>
                      {c.website && (
                        <button onClick={(e) => { e.stopPropagation(); openSite(c.website); }} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors" data-testid={`club-web-${c.id}`}>
                          <ExternalLink className="w-3 h-3" /> web
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 md:hidden" /> {c.city}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground truncate">
                    <Mail className="w-3.5 h-3.5 md:hidden shrink-0" /> {c.email}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Phone className="w-3.5 h-3.5 md:hidden" /> {c.phone}
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </section>

      <AllMembersDirectory />

      <footer className="border-t border-border py-10 text-center text-sm text-muted-foreground">
        UPUS CG — Udruženje plesnih umjetnika i stvaralaca Crne Gore · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
